/**
 * Beyza Fashion — product page enhancements.
 * Loaded once by sections/product-information.liquid (type="module").
 *
 * Works on top of the theme's own components and never replaces them:
 *  - Gallery: keyboard arrows, an accessible zoom button (opens the theme's zoom dialog) and the
 *    wishlist heart over the image.
 *  - Variants: option values the theme marks unavailable (data-option-available="false") cannot be chosen.
 *  - Add to cart: "Adding… / Added to cart" text states. The theme's product form still sends the
 *    request, updates the cart count and opens the cart drawer. Double submits are blocked while adding.
 *  - Keeps Beyza blocks (badges, trust info, wishlist data) in sync using the HTML the theme already
 *    fetched for its variant:update event — no extra requests.
 *  - "Read more" opens the Description accordion; the rating scrolls to the Judge.me reviews.
 *  - Recently viewed products (localStorage "beyza_recently_viewed").
 */
import { ThemeEvents } from '@theme/events';

const RV_KEY = 'beyza_recently_viewed';
const RV_MAX = 16;

const prefersReducedMotion = () =>
  !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');

function paintWishlist(scope) {
  if (window.BeyzaWishlist && typeof window.BeyzaWishlist.paint === 'function') {
    window.BeyzaWishlist.paint(scope || document);
  }
}

/* ------------------------------------------------------------------ */
/* Live region                                                         */
/* ------------------------------------------------------------------ */
function announce(root, message) {
  if (!message) return;
  let region = root.querySelector('[data-beyza-live]');
  if (!region) {
    region = document.createElement('div');
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('data-beyza-live', '');
    root.appendChild(region);
  }
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 60);
}

/* ------------------------------------------------------------------ */
/* Gallery                                                             */
/* ------------------------------------------------------------------ */
const ZOOM_ICON =
  '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="10.5" cy="10.5" r="6.25" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="m15.25 15.25 5 5M10.5 8v5M8 10.5h5" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';

function syncOverlayData(root) {
  const main = root.querySelector('.beyza-product__wishlist[data-beyza-wishlist]');
  if (!main) return;
  const attrs = ['data-variant-id', 'data-product-image', 'data-product-price', 'data-product-compare'];
  root.querySelectorAll('.beyza-product__wishlist-overlay').forEach((btn) => {
    attrs.forEach((name) => {
      if (main.hasAttribute(name)) btn.setAttribute(name, main.getAttribute(name) || '');
      else btn.removeAttribute(name);
    });
  });
}

function enhanceGallery(root) {
  const gallery = root.querySelector('media-gallery');
  if (!gallery) return;

  const slides = gallery.querySelectorAll('slideshow-slide');
  const scroller = gallery.querySelector('slideshow-slides');
  const stage = gallery.querySelector('slideshow-container') || gallery;
  gallery.classList.add('beyza-product__gallery');
  gallery.classList.toggle('beyza-product__gallery--single', slides.length < 2);

  if (scroller && slides.length > 1 && !scroller.hasAttribute('data-beyza-a11y')) {
    scroller.setAttribute('data-beyza-a11y', '');
    scroller.setAttribute('tabindex', '0');
    scroller.setAttribute('role', 'region');
    scroller.setAttribute('aria-roledescription', 'carousel');
    scroller.setAttribute('aria-label', root.dataset.labelGallery || 'Product images, use the arrow keys to browse');
  }

  if (!stage.querySelector(':scope > .beyza-product__media-wish')) {
    const template = root.querySelector('template[data-beyza-wishlist-overlay]');
    if (template) {
      const holder = document.createElement('div');
      holder.className = 'beyza-product__media-wish';
      holder.appendChild(template.content.cloneNode(true));
      stage.appendChild(holder);
    }
  }

  const zoomDialog = gallery.querySelector('zoom-dialog');
  if (zoomDialog && slides.length && !stage.querySelector(':scope > .beyza-product__media-zoom')) {
    const zoom = document.createElement('button');
    zoom.type = 'button';
    zoom.className = 'beyza-product__media-zoom';
    zoom.setAttribute('aria-label', `${root.dataset.labelZoom || 'Zoom'}`);
    zoom.setAttribute('aria-haspopup', 'dialog');
    zoom.innerHTML = ZOOM_ICON;
    zoom.addEventListener('click', (event) => {
      const liveGallery = zoom.closest('media-gallery');
      const slideshow = liveGallery && liveGallery.querySelector('slideshow-component');
      const index = slideshow && typeof slideshow.current === 'number' ? slideshow.current : 0;
      if (liveGallery && typeof liveGallery.zoom === 'function') {
        liveGallery.zoom(index, event);
      } else {
        const dialog = liveGallery && liveGallery.querySelector('zoom-dialog');
        if (dialog && typeof dialog.open === 'function') dialog.open(index, event);
      }
    });
    stage.appendChild(zoom);
  }

  syncOverlayData(root);
  paintWishlist(stage);
}

function setupGalleryKeys(root) {
  root.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target || target.closest('dialog, input, select, textarea')) return;
    const gallery = target.closest('media-gallery');
    if (!gallery) return;
    const slideshow = gallery.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow.next !== 'function') return;
    if (gallery.querySelectorAll('slideshow-slide').length < 2) return;
    event.preventDefault();
    const rtl = document.documentElement.dir === 'rtl';
    const forward = (event.key === 'ArrowRight') !== rtl;
    if (forward) slideshow.next();
    else slideshow.previous();
  });
}

/* ------------------------------------------------------------------ */
/* Variants: unavailable values cannot be selected                     */
/* ------------------------------------------------------------------ */
function setupVariantGuard(root) {
  const soldOut = root.dataset.labelSoldOut || 'Sold out';

  root.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const label = target && target.closest('variant-picker .variant-option__button-label');
      if (!label) return;
      const input = label.querySelector('input[type="radio"]');
      if (!input || input.checked || input.dataset.optionAvailable !== 'false') return;
      event.preventDefault();
      event.stopPropagation();
      announce(root, `${input.value}: ${soldOut}`);
    },
    true
  );

  root.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
      if (!input.closest('variant-picker') || input.dataset.optionAvailable !== 'false') return;
      event.stopImmediatePropagation();
      const fieldset = input.closest('fieldset');
      const previous = fieldset && fieldset.querySelector('input[data-current-checked="true"]');
      if (previous && previous !== input) previous.checked = true;
      else input.checked = false;
      announce(root, `${input.value}: ${soldOut}`);
    },
    true
  );
}

/* ------------------------------------------------------------------ */
/* Add to cart text states                                             */
/* ------------------------------------------------------------------ */
function setupAddToCartStates(root) {
  const productId = String(root.dataset.productId || '');
  const labels = {
    adding: root.dataset.labelAdding || 'Adding…',
    added: root.dataset.labelAdded || 'Added to cart',
  };
  let state = 'idle';
  let resetTimer = 0;
  let safetyTimer = 0;

  const buttons = () =>
    [
      root.querySelector('product-form-component [ref="addToCartButton"]'),
      root.querySelector('.sticky-add-to-cart__button'),
    ].filter(Boolean);

  const labelOf = (btn) =>
    btn.querySelector('.add-to-cart-text__content > span > span') ||
    btn.querySelector('.add-to-cart-text__content > span:first-child');

  function setState(next) {
    window.clearTimeout(resetTimer);
    window.clearTimeout(safetyTimer);
    state = next;
    buttons().forEach((btn) => {
      const label = labelOf(btn);
      if (next === 'idle') {
        if (label && btn.dataset.beyzaIdle) label.textContent = btn.dataset.beyzaIdle;
        delete btn.dataset.beyzaIdle;
        btn.removeAttribute('data-beyza-state');
        btn.removeAttribute('aria-busy');
        return;
      }
      if (label && !btn.dataset.beyzaIdle) btn.dataset.beyzaIdle = label.textContent || '';
      if (label) label.textContent = labels[next];
      btn.setAttribute('data-beyza-state', next);
      if (next === 'adding') btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
    });
    if (next === 'adding') safetyTimer = window.setTimeout(() => setState('idle'), 8000);
    if (next === 'added') resetTimer = window.setTimeout(() => setState('idle'), 2200);
  }

  root.addEventListener(
    'submit',
    (event) => {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      if (!form || !form.closest('product-form-component')) return;
      if (state === 'adding') {
        // A request is already on its way: never send a second one.
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const main = form.querySelector('[ref="addToCartButton"]');
      if (!main || main.disabled) return;
      setState('adding');
    },
    true
  );

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    const data = event.detail && event.detail.data;
    if (!data || data.source !== 'product-form-component') return;
    if (productId && data.productId && String(data.productId) !== productId) return;
    if (state !== 'adding') return;
    setState(data.didError ? 'idle' : 'added');
  });

  document.addEventListener(ThemeEvents.cartError, () => {
    if (state === 'adding') setState('idle');
  });

  return () => setState('idle');
}

/* ------------------------------------------------------------------ */
/* Variant update: keep Beyza blocks in sync                           */
/* ------------------------------------------------------------------ */
function syncFromVariantUpdate(root, html) {
  root.querySelectorAll('[data-beyza-sync]').forEach((el) => {
    const key = el.getAttribute('data-beyza-sync');
    if (!key) return;
    const fresh = html.querySelector(`[data-beyza-sync="${CSS.escape(key)}"]`);
    if (!fresh) return;
    if (el.getAttribute('data-beyza-sync-mode') === 'attrs') {
      Array.from(fresh.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-') && attr.name !== 'data-beyza-sync') el.setAttribute(attr.name, attr.value);
      });
      if (!fresh.hasAttribute('data-product-compare')) el.removeAttribute('data-product-compare');
    } else {
      el.replaceWith(document.importNode(fresh, true));
    }
  });
}

/* ------------------------------------------------------------------ */
/* Read more / size guide / rating                                     */
/* ------------------------------------------------------------------ */
function findAccordionRow(root, heading) {
  const wanted = (heading || '').replace(/\s+/g, ' ').trim().toLowerCase();
  if (!wanted) return null;
  const rows = Array.from(root.querySelectorAll('accordion-custom details'));
  return (
    rows.find((details) => {
      const summary = details.querySelector('summary');
      const text = summary ? (summary.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase() : '';
      return text === wanted || text.startsWith(wanted);
    }) || null
  );
}

function setupClicks(root) {
  root.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const readMore = target.closest('[data-beyza-readmore]');
    if (readMore) {
      const wrap = readMore.closest('[data-beyza-excerpt]');
      if (!wrap) return;
      const row = findAccordionRow(root, wrap.getAttribute('data-target-heading'));
      if (row) {
        row.open = true;
        const summary = row.querySelector('summary');
        if (summary) {
          summary.scrollIntoView({ behavior: scrollBehavior(), block: 'center' });
          summary.focus({ preventScroll: true });
        }
        return;
      }
      const template = wrap.querySelector('template[data-beyza-excerpt-full]');
      const text = wrap.querySelector('[data-beyza-excerpt-text]');
      if (template && text) {
        text.replaceWith(template.content.cloneNode(true));
        readMore.remove();
      }
      return;
    }

    const sizeGuideButton = target.closest('[data-beyza-open-size-guide]');
    if (sizeGuideButton) {
      const trigger = root.querySelector('beyza-size-guide [data-bsg-open]');
      if (trigger instanceof HTMLElement) trigger.click();
      return;
    }

    const rating = target.closest('[data-beyza-rating-link]');
    if (rating) {
      const id = decodeURIComponent((rating.getAttribute('href') || '').replace(/^#/, ''));
      const reviews = id ? document.getElementById(id) : null;
      if (!reviews) return;
      event.preventDefault();
      reviews.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
      if (!reviews.hasAttribute('tabindex')) reviews.setAttribute('tabindex', '-1');
      reviews.focus({ preventScroll: true });
    }
  });
}

function prepareLinks(root) {
  const hasSizeGuide = !!root.querySelector('beyza-size-guide [data-bsg-open]');
  root.querySelectorAll('[data-beyza-open-size-guide]').forEach((btn) => {
    btn.hidden = !hasSizeGuide;
  });

  root.querySelectorAll('[data-beyza-rating-link]').forEach((link) => {
    const id = decodeURIComponent((link.getAttribute('href') || '').replace(/^#/, ''));
    if (id && document.getElementById(id)) return;
    // No reviews widget on this page: keep the rating visible but not as a dead link.
    const span = document.createElement('span');
    span.className = link.className;
    span.setAttribute('role', 'img');
    span.setAttribute('aria-label', link.getAttribute('aria-label') || '');
    span.innerHTML = link.innerHTML;
    link.replaceWith(span);
  });
}

/* ------------------------------------------------------------------ */
/* Recently viewed                                                     */
/* ------------------------------------------------------------------ */
function readRecentlyViewed() {
  try {
    const data = JSON.parse(window.localStorage.getItem(RV_KEY) || '[]');
    return Array.isArray(data) ? data.filter((item) => item && /^\d+$/.test(String(item.id))) : [];
  } catch (e) {
    return [];
  }
}

function recordRecentlyViewed(id, handle) {
  if (!id || !/^\d+$/.test(String(id))) return;
  try {
    const list = readRecentlyViewed().filter((item) => String(item.id) !== String(id));
    list.unshift({ id: String(id), handle: handle || '', t: Date.now() });
    window.localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, RV_MAX)));
  } catch (e) {
    /* storage unavailable (private mode): recently viewed simply stays hidden */
  }
}

function renderRecentlyViewed(section) {
  if (!section || section.hasAttribute('data-beyza-rv-done')) return;
  section.setAttribute('data-beyza-rv-done', '');
  const current = String(section.dataset.productId || '');
  const limit = parseInt(section.dataset.limit || '4', 10) || 4;
  const ids = readRecentlyViewed()
    .map((item) => String(item.id))
    .filter((id) => id !== current)
    .slice(0, limit);
  if (!ids.length) return;

  const url = new URL(section.dataset.searchUrl || '/search', window.location.origin);
  url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('type', 'product');
  url.searchParams.set('section_id', 'beyza-recently-viewed');

  fetch(url.toString(), { credentials: 'same-origin' })
    .then((response) => (response.ok ? response.text() : ''))
    .then((text) => {
      if (!text) return;
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const items = Array.from(doc.querySelectorAll('[data-beyza-rv-results] > .beyza-rv__item'));
      if (!items.length) return;
      items.sort((a, b) => ids.indexOf(a.dataset.productId || '') - ids.indexOf(b.dataset.productId || ''));
      const list = section.querySelector('[data-beyza-rv-list]');
      if (!list) return;
      items.forEach((item) => {
        item.setAttribute('role', 'listitem');
        list.appendChild(document.importNode(item, true));
      });
      section.hidden = false;
      paintWishlist(section);
    })
    .catch(() => {
      /* network error: section stays hidden */
    });
}

function setupRecentlyViewed(root) {
  const section = document.querySelector('[data-beyza-recently-viewed]');
  if (section) {
    const watch = section.closest('.shopify-section') || section.parentElement || section;
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            renderRecentlyViewed(section);
          }
        },
        { rootMargin: '600px 0px' }
      );
      observer.observe(watch);
    } else {
      renderRecentlyViewed(section);
    }
  }
  // Remember this product after reading the list, so it is never shown as its own "recently viewed".
  recordRecentlyViewed(root.dataset.productId, root.dataset.productHandle);
}

/* ------------------------------------------------------------------ */
/* Init                                                                */
/* ------------------------------------------------------------------ */
function init(root) {
  if (!(root instanceof HTMLElement) || root.hasAttribute('data-beyza-ready')) return;
  root.setAttribute('data-beyza-ready', '');

  enhanceGallery(root);
  setupGalleryKeys(root);
  setupVariantGuard(root);
  const resetAddToCart = setupAddToCartStates(root);
  setupClicks(root);
  prepareLinks(root);

  root.addEventListener(ThemeEvents.variantUpdate, (event) => {
    const html = event.detail && event.detail.data && event.detail.data.html;
    if (!html) return;
    syncFromVariantUpdate(root, html);
    syncOverlayData(root);
    resetAddToCart();
    // The theme swaps the media gallery in its own listener; enhance the new one on the next frame.
    window.requestAnimationFrame(() => {
      enhanceGallery(root);
      prepareLinks(root);
      paintWishlist(root);
    });
  });

  setupRecentlyViewed(root);
  paintWishlist(root);
}

function start(scope) {
  (scope || document).querySelectorAll('[data-beyza-product]').forEach((root) => init(/** @type {HTMLElement} */ (root)));
}

start(document);

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (target) start(target);
});
